/**
 * El logo de Tennis Up Club.
 *
 * VA EN LÍNEA Y NO COMO `<img>`, y no es un capricho: el logotipo completo
 * lleva la palabra «Tennis Up Club» escrita en Inter, y un SVG cargado con
 * `<img src>` es un documento aparte que NO ve las fuentes de la página. La
 * app carga Inter desde Google Fonts para todo lo demás, pero adentro de un
 * `<img>` esa fuente no llega: el navegador caía en Arial y el logotipo se
 * dibujaba con otras letras y otro interletrado —el `letter-spacing` está
 * calibrado para Inter— sin que nada avisara. En línea hereda la fuente de la
 * página y se dibuja como es.
 *
 * Y DE PASO SE ARREGLA LO OTRO: antes eran dos archivos por variante —claro y
 * oscuro— y se descargaban LOS DOS para esconder uno con CSS. Ahora es un
 * solo dibujo y el color del tema entra por `currentColor`, que es lo que ya
 * sabe resolver los tres lugares de donde puede venir el tema (la cuenta, el
 * atributo del documento, o lo que pida el sistema si nadie eligió).
 *
 * LO QUE NO CAMBIA es cuál se usa dónde: el isotipo solo para la barra, el
 * ícono y el avatar; el logotipo completo donde hay lugar para leerlo.
 *
 * `alt` vacío A PROPÓSITO cuando va acompañado: en la barra de arriba, al
 * lado del nombre de quien entró, «Tennis Up Club» ya lo dice el título de la
 * página y repetirlo es ruido para quien escucha.
 */
import { useId } from 'react';

export function Logo({
  size = 36,
  alt = '',
  variant = 'mark',
}: {
  size?: number;
  alt?: string;
  /** `mark` es solo el isotipo; `full` lleva además la palabra, al lado. */
  variant?: 'mark' | 'full';
}): JSX.Element {
  /*
    UN ID POR CADA LOGO EN LA PÁGINA. El degradado se referencia por `id`, y
    con dos logos dibujados a la vez —pasa: la barra y el cuadro público— dos
    `id="top"` iguales hacen que el segundo tome el degradado del primero. Con
    `useId` cada uno tiene el suyo.
  */
  const idDegradado = `logo-top-${useId()}`;

  /*
    LA TINTA ES `currentColor`: el azul del logo en claro, el casi blanco en
    oscuro. Lo fija el CSS con `.brand-mark { color: … }` en cada tema, que es
    donde ya viven todos los colores de la app.
  */
  const degradado = (
    <defs>
      <linearGradient id={idDegradado} x1="0" y1="0" x2="1" y2="0">
        <stop offset="0" stopColor="#C7F000" />
        <stop offset="0.32" stopColor="#8DBA00" />
        <stop offset="0.58" stopColor="currentColor" />
        <stop offset="1" stopColor="currentColor" />
      </linearGradient>
    </defs>
  );

  // El isotipo: la línea de arriba —la red, en degradado— y el cuerpo debajo.
  const isotipo = (
    <>
      <path d="M92 120 H420 L376 176 H136 Z" fill={`url(#${idDegradado})`} />
      <path d="M218 196 H294 V390 L256 422 L218 390 Z" fill="currentColor" />
    </>
  );

  /*
    `role` y `aria-label` SEGÚN HAYA TEXTO O NO. Con `alt` vacío el logo es
    decorativo y se esconde de quien escucha; con texto es una imagen con
    nombre. Un SVG sin `role="img"` lo anuncian distinto los lectores de
    pantalla, y algunos leen las etiquetas de adentro.
  */
  const accesible =
    alt === ''
      ? ({ 'aria-hidden': true } as const)
      : ({ role: 'img', 'aria-label': alt } as const);

  if (variant === 'mark') {
    return (
      <span className="brand-mark" style={{ height: size }}>
        <svg viewBox="0 0 512 512" height={size} width={size} {...accesible}>
          {degradado}
          {isotipo}
        </svg>
      </span>
    );
  }

  /*
    EL LOGOTIPO COMPLETO ES HORIZONTAL. El ancho sale del alto y no al revés:
    quien lo pide dice cuánto alto tiene disponible.

    EL ANCHO DEL `viewBox` ESTÁ MEDIDO, Y HAY QUE MEDIRLO CON LA FUENTE YA
    CARGADA. Acá estuvo 1400 y le faltaba la «b» de «Club»: la medición que
    dio ese número se hizo antes de que Inter terminara de cargar, así que
    midió la fuente de respaldo —más angosta— y dijo que el texto terminaba
    en 1368. Con Inter de verdad, esperando `document.fonts.ready`, el texto
    arranca en x=360 y mide 1148: termina en 1508.

    1560 le deja 52 de aire, el mismo margen proporcional que tenía pensado
    el número viejo. Ni más, porque el hueco a la derecha desalinea el logo
    de todo lo que tenga al lado.

    Si cambia el texto hay que volver a medirlo —CON la fuente cargada—: el
    recorte de un SVG no avisa, simplemente falta una letra, y en «Club» la
    que falta es la última.
  */
  return (
    <span className="brand-logo" style={{ height: size }}>
      <svg
        viewBox="0 0 1560 360"
        height={size}
        width={(size * 1560) / 360}
        {...accesible}
      >
        {degradado}
        <g transform="translate(50,35) scale(.55)">{isotipo}</g>
        {/*
          `font-family` heredada de la página y no escrita acá: es lo que hace
          que Inter llegue. La familia de respaldo la define `--font`.

          «Up» EN VERDE, como en el kit: es la sílaba que la marca acentúa. El
          resto va con la tinta del tema para que se lea en claro y en oscuro.
        */}
        <text
          x="360"
          y="220"
          fontSize="150"
          fontWeight="700"
          letterSpacing="-4"
          fill="currentColor"
        >
          Tennis
          <tspan fill="#8DBA00"> Up</tspan>
          <tspan fontWeight="500"> Club</tspan>
        </text>
      </svg>
    </span>
  );
}
